import ListActionsMenu from "../documents/ListActionsMenu";
import SifenReportModal from "../sifen-reports/SifenReportModal";
import {
  CButton,
  CButtonGroup,
  CDropdown,
  CDropdownItem,
  CDropdownToggle,
  CTooltip,
} from "@coreui/react";
import {
  INVOICE_STATUS_APPROVED_BY_SET,
  INVOICE_STATUS_CANCELLATION_FAILED,
  INVOICE_STATUS_DRAFT,
  INVOICE_STATUS_ERROR,
  INVOICE_STATUS_NOMINATED,
  INVOICE_STATUS_PENDING,
  INVOICE_STATUS_RECOVERABLE_ERROR,
  INVOICE_STATUS_RESEND_REQUESTED,
  INVOICE_STATUS_SET_UNKNOWN_ERROR,
  INVOICE_STATUS_UPLOADED_TO_SET,
  Invoice,
} from "../../models/invoice";
import React, { useEffect, useState } from "react";
import { useSub } from "../../hooks/pub-sub";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  INVOICES_DESTROY,
  INVOICES_RETRIEVE,
  INVOICES_UPDATE,
} from "../../auth/permissions";
import { CLIENT_TYPE_NOT_NOMINATED } from "../../models/client";

const MAX_VISIBLE_ACTIONS = 3;

interface InvoiceCrudAtionsProps {
  invoice: Invoice;
  onCancellationClick: (_: Invoice) => void;
  onCancellationRetryClick: (_: Invoice) => void;
  onCancellationRevertClick: (_: Invoice) => void;
  onReadClick: (_: Invoice) => void;
  onUpdateClick: (_: Invoice) => void;
  onDeleteClick: (_: Invoice) => void;
  onConsultByCdcClick: (_: Invoice) => void;
  onResendClick: (_: Invoice) => void;
  onSendEmailClick: (_: Invoice) => void;
  onNominateClick: (_: Invoice) => void;
  onHistoryClick: (_: Invoice) => void;
  onConfirmDraftClick: (_: Invoice) => void;
  onDiscardDraftClick: (_: Invoice) => void;
  onRegenerateKudeClick: (_: Invoice) => void;
}

const InvoiceCrudActions: React.FC<InvoiceCrudAtionsProps> = ({
  invoice,
  onReadClick,
  onUpdateClick,
  onDeleteClick,
  onConsultByCdcClick,
  onCancellationClick,
  onCancellationRetryClick,
  onCancellationRevertClick,
  onResendClick,
  onSendEmailClick,
  onNominateClick,
  onHistoryClick,
  onConfirmDraftClick,
  onDiscardDraftClick,
  onRegenerateKudeClick,
}) => {
  const [showSifenReport, setShowSifenReport] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(invoice);

  useEffect(() => {
    setCurrentInvoice(invoice);
  }, [invoice]);

  useSub(`invoice_${invoice.id}`, (invoice: Invoice) => {
    setCurrentInvoice(invoice);
  });

  const cancelButton = (
    <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
      <CTooltip content="Anular">
        <CButton
          className="text-white"
          color="secondary"
          onClick={() => {
            onCancellationClick(currentInvoice);
          }}
        >
          <i className="fa fa-ban"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const editButton = (
    <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
      <CTooltip content="Editar">
        <CButton
          className="text-white"
          color="warning"
          onClick={() => {
            onUpdateClick(currentInvoice);
          }}
        >
          <i className="fa fa-pencil"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const deleteButton = (
    <PermissionRequiredComponent permissionName={INVOICES_DESTROY}>
      <CTooltip content="Eliminar">
        <CButton
          className="text-white"
          color="danger"
          onClick={() => {
            onDeleteClick(currentInvoice);
          }}
        >
          <i className="fa fa-trash"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const invoiceDateTime = new Date(currentInvoice.createdAt!).getTime();
  const now = new Date().getTime();

  const invoiceOlderThan3Days = invoiceDateTime < now - 3 * 24 * 60 * 60 * 1000;

  const shouldShowCancelButton =
    (currentInvoice.status === INVOICE_STATUS_APPROVED_BY_SET ||
      currentInvoice.status === INVOICE_STATUS_NOMINATED) &&
    !invoiceOlderThan3Days;

  const shouldShowEditAndDeleteButton =
    (currentInvoice.status === INVOICE_STATUS_PENDING ||
      currentInvoice.status === INVOICE_STATUS_ERROR ||
      currentInvoice.status === INVOICE_STATUS_SET_UNKNOWN_ERROR ||
      currentInvoice.setResponseCode === "1001" ||
      currentInvoice.setResponseCode === "4003") &&
    currentInvoice.status !== INVOICE_STATUS_RECOVERABLE_ERROR;

  const consultByCdcButton = (
    <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
      <CTooltip content="Consultar por CDC">
        <CButton
          className="text-white"
          color="primary"
          onClick={() => {
            onConsultByCdcClick(invoice);
          }}
        >
          <i className="fa fa-question-circle"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowConsultByCdcButton =
    currentInvoice.status === INVOICE_STATUS_UPLOADED_TO_SET ||
    currentInvoice.status === INVOICE_STATUS_ERROR ||
    currentInvoice.status === INVOICE_STATUS_RESEND_REQUESTED ||
    currentInvoice.setResponseCode === "1001" ||
    currentInvoice.setResponseCode === "4003";

  const resendButton = (
    <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
      <CTooltip content="Reenviar">
        <CButton
          className="text-white"
          color="success"
          onClick={() => {
            onResendClick(currentInvoice);
          }}
        >
          <i className="fa fa-refresh"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowResendButton =
    currentInvoice.status === INVOICE_STATUS_ERROR;

  const nominateButton = (
    <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
      <CTooltip content="Nominar">
        <CButton
          className="text-white"
          color="info"
          onClick={() => {
            onNominateClick(invoice);
          }}
        >
          <i className="fa fa-person"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowNominateButton =
    currentInvoice.status === INVOICE_STATUS_APPROVED_BY_SET &&
    currentInvoice.clientType === CLIENT_TYPE_NOT_NOMINATED;

  const sendEmailButton = (
    <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
      <CTooltip
        content={invoice.emailSent ? "Reenviar Correo" : "Enviar Correo"}
      >
        <CButton
          className="text-white"
          color="primary"
          onClick={() => {
            onSendEmailClick(invoice);
          }}
        >
          <i className="fa fa-envelope"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowSendEmailButton =
    invoice.status === INVOICE_STATUS_APPROVED_BY_SET;

  const retryCancellationButton = (
    <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
      <CTooltip content="Reintentar Anulación">
        <CButton
          className="text-white"
          color="warning"
          onClick={() => {
            onCancellationRetryClick(currentInvoice);
          }}
        >
          <i className="fa fa-rotate-right"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowRetryCancellationButton =
    invoice.status === INVOICE_STATUS_CANCELLATION_FAILED;

  const revertCancellationButton = (
    <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
      <CTooltip content="Revertir Anulación">
        <CButton
          className="text-white"
          color="success"
          onClick={() => {
            onCancellationRevertClick(currentInvoice);
          }}
        >
          <i className="fa fa-backward"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowRevertCancellationButton =
    invoice.status === INVOICE_STATUS_CANCELLATION_FAILED;

  const confirmDraftButton = (
    <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
      <CTooltip content="Confirmar Borrador">
        <CButton
          className="text-white"
          color="success"
          onClick={() => {
            onConfirmDraftClick(currentInvoice);
          }}
        >
          <i className="fa fa-check"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const discardDraftButton = (
    <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
      <CTooltip content="Descartar Borrador">
        <CButton
          className="text-white"
          color="danger"
          onClick={() => {
            onDiscardDraftClick(currentInvoice);
          }}
        >
          <i className="fa fa-times"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowDraftActions = currentInvoice.status === INVOICE_STATUS_DRAFT;

  const regenerateKudeButton = (
    <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
      <CTooltip content="Regenerar KUDE">
        <CButton
          className="text-white"
          color="secondary"
          onClick={() => {
            onRegenerateKudeClick(currentInvoice);
          }}
        >
          <i className="fa fa-file-pdf"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowRegenerateKudeButton = currentInvoice.cdc !== null && currentInvoice.cdc !== undefined;

  const readButton = (
    <PermissionRequiredComponent permissionName={INVOICES_RETRIEVE}>
      <CTooltip content="Ver Datos">
        <CButton
          className="text-white"
          color="info"
          onClick={() => {
            onReadClick(currentInvoice);
          }}
        >
          <i className="fa fa-eye"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const historyButton = (
    <PermissionRequiredComponent permissionName={INVOICES_RETRIEVE}>
      <CTooltip content="Ver Historial">
        <CButton
          className="text-white"
          color="dark"
          onClick={() => {
            onHistoryClick(currentInvoice);
          }}
        >
          <i className="fa fa-history"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const allActions = [
    {
      element: readButton,
      shouldShow: true,
      label: "Ver Datos",
      icon: "fa-eye",
      permission: INVOICES_RETRIEVE,
      onClick: () => onReadClick(currentInvoice),
    },
    {
      element: historyButton,
      shouldShow: !shouldShowDraftActions,
      label: "Ver Historial",
      icon: "fa-history",
      permission: INVOICES_RETRIEVE,
      onClick: () => onHistoryClick(currentInvoice),
    },
    {
      element: confirmDraftButton,
      shouldShow: shouldShowDraftActions,
      label: "Confirmar Borrador",
      icon: "fa-check",
      permission: INVOICES_UPDATE,
      onClick: () => onConfirmDraftClick(currentInvoice),
    },
    {
      element: discardDraftButton,
      shouldShow: shouldShowDraftActions,
      label: "Descartar Borrador",
      icon: "fa-times",
      permission: INVOICES_UPDATE,
      onClick: () => onDiscardDraftClick(currentInvoice),
    },
    {
      element: editButton,
      shouldShow: shouldShowDraftActions || shouldShowEditAndDeleteButton,
      label: "Editar",
      icon: "fa-pencil",
      permission: INVOICES_UPDATE,
      onClick: () => onUpdateClick(currentInvoice),
    },
    {
      element: cancelButton,
      shouldShow: shouldShowCancelButton && !shouldShowDraftActions,
      label: "Anular",
      icon: "fa-ban",
      permission: INVOICES_UPDATE,
      onClick: () => onCancellationClick(currentInvoice),
    },
    {
      element: deleteButton,
      shouldShow: shouldShowEditAndDeleteButton && !shouldShowDraftActions,
      label: "Eliminar",
      icon: "fa-trash",
      permission: INVOICES_DESTROY,
      onClick: () => onDeleteClick(currentInvoice),
    },
    {
      element: consultByCdcButton,
      shouldShow: shouldShowConsultByCdcButton && !shouldShowDraftActions,
      label: "Consultar por CDC",
      icon: "fa-question-circle",
      permission: INVOICES_UPDATE,
      onClick: () => onConsultByCdcClick(invoice),
    },
    {
      element: resendButton,
      shouldShow: shouldShowResendButton && !shouldShowDraftActions,
      label: "Reenviar",
      icon: "fa-refresh",
      permission: INVOICES_UPDATE,
      onClick: () => onResendClick(currentInvoice),
    },
    {
      element: retryCancellationButton,
      shouldShow: shouldShowRetryCancellationButton && !shouldShowDraftActions,
      label: "Reintentar Anulación",
      icon: "fa-rotate-right",
      permission: INVOICES_UPDATE,
      onClick: () => onCancellationRetryClick(currentInvoice),
    },
    {
      element: revertCancellationButton,
      shouldShow: shouldShowRevertCancellationButton && !shouldShowDraftActions,
      label: "Revertir Anulación",
      icon: "fa-backward",
      permission: INVOICES_UPDATE,
      onClick: () => onCancellationRevertClick(currentInvoice),
    },
    {
      element: nominateButton,
      shouldShow: shouldShowNominateButton && !shouldShowDraftActions,
      label: "Nominar",
      icon: "fa-person",
      permission: INVOICES_UPDATE,
      onClick: () => onNominateClick(invoice),
    },
    {
      element: sendEmailButton,
      shouldShow: shouldShowSendEmailButton && !shouldShowDraftActions,
      label: invoice.emailSent ? "Reenviar Correo" : "Enviar Correo",
      icon: "fa-envelope",
      permission: INVOICES_UPDATE,
      onClick: () => onSendEmailClick(invoice),
    },
    {
      element: regenerateKudeButton,
      shouldShow: shouldShowRegenerateKudeButton && !shouldShowDraftActions,
      label: "Regenerar KUDE",
      icon: "fa-file-pdf",
      permission: INVOICES_UPDATE,
      onClick: () => onRegenerateKudeClick(currentInvoice),
    },
    {
      element: (
        <PermissionRequiredComponent permissionName={INVOICES_UPDATE}>
          <CTooltip content="Preparar reporte para DNIT">
            <CButton color="warning" aria-label="Preparar reporte para DNIT" onClick={() => setShowSifenReport(true)}>
              <i className="fa fa-flag"></i>
            </CButton>
          </CTooltip>
        </PermissionRequiredComponent>
      ),
      shouldShow: currentInvoice.status === "cancelled",
      label: "Preparar reporte para DNIT",
      icon: "fa-flag",
      permission: INVOICES_UPDATE,
      onClick: () => setShowSifenReport(true),
    },
  ];

  const visibleActions = allActions.filter((a) => a.shouldShow);
  const shouldCollapse = visibleActions.length > MAX_VISIBLE_ACTIONS;
  const primaryActions = shouldCollapse
    ? visibleActions.slice(0, MAX_VISIBLE_ACTIONS - 1)
    : visibleActions;
  const overflowActions = shouldCollapse
    ? visibleActions.slice(MAX_VISIBLE_ACTIONS - 1)
    : [];

  return (
    <td className="text-center">
      <CButtonGroup>
        {primaryActions.map((a, i) => (
          <React.Fragment key={i}>{a.element}</React.Fragment>
        ))}
        {shouldCollapse && (
          <CDropdown className="btn-group">
            <CTooltip content="Más opciones">
              <CDropdownToggle
                color="primary"
                className="text-white"
                caret={false}
                style={{ borderTopRightRadius: "0.25rem", borderBottomRightRadius: "0.25rem" }}
              >
                <i className="fa fa-ellipsis-v"></i>
              </CDropdownToggle>
            </CTooltip>
            <ListActionsMenu>
              {overflowActions.map((a, i) => (
                <PermissionRequiredComponent
                  key={i}
                  permissionName={a.permission}
                >
                  <CDropdownItem onClick={a.onClick}>
                    <i className={`fa ${a.icon} mr-2`}></i>
                    {a.label}
                  </CDropdownItem>
                </PermissionRequiredComponent>
              ))}
            </ListActionsMenu>
          </CDropdown>
        )}
      </CButtonGroup>
      {showSifenReport && <SifenReportModal
        show={showSifenReport}
        documentType="electronic_invoice"
        documentId={currentInvoice.id}
        onClose={() => setShowSifenReport(false)}
      />}
    </td>
  );
};

export default InvoiceCrudActions;
